<template>
  <Transition name="modal">
    <div v-if="isOpen" class="fixed inset-0 z-50 overflow-y-auto" @click.self="$emit('close')">
      <div class="flex sm:items-center sm:justify-center min-h-screen sm:px-4 sm:pt-4 sm:pb-20 text-center">
        <div class="fixed inset-0 transition-opacity bg-black bg-opacity-50 dark:bg-opacity-50" @click="$emit('close')"></div>
        <div class="inline-block align-bottom sm:align-middle bg-white dark:bg-gray-800 sm:rounded-lg text-left overflow-hidden shadow-xl transform transition-all min-h-screen w-full fixed inset-x-0 top-0 sm:static sm:min-h-0 sm:max-w-2xl sm:w-full flex flex-col">
          <div class="bg-gradient-to-tr from-primary to-secondary px-6 py-5 flex items-center justify-between">
            <div class="flex items-center gap-3 min-w-0">
              <div class="h-10 w-10 rounded-xl bg-white/20 flex items-center justify-center shrink-0">
                <FileText class="h-5 w-5 text-white" />
              </div>
              <div class="min-w-0">
                <h3 class="text-lg font-bold text-white truncate">{{ document?.title || 'Document Details' }}</h3>
                <p class="text-sm text-white/70 truncate">{{ document?.status }}</p>
              </div>
            </div>
            <button @click="$emit('close')" class="shrink-0 p-1.5 rounded-lg hover:bg-white/10 text-white/80 hover:text-white transition-colors">
              <X class="h-5 w-5" />
            </button>
          </div>
          <div class="px-4 py-5 sm:p-6 max-h-[calc(100vh-16rem)] overflow-y-auto">
            <!-- Basic Information -->
            <div class="bg-gray-50 dark:bg-gray-700/30 p-5 rounded-xl border border-gray-200 dark:border-gray-600 mb-5">
              <h4 class="text-sm font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-4 border-b border-gray-200 dark:border-gray-600 pb-2">
                <BookCopy class="h-4 w-4 inline" />
                Document Information
              </h4>
              <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div class="md:col-span-2">
                  <p class="text-xs font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wide">Title</p>
                  <p class="text-sm font-medium text-gray-800 dark:text-gray-200 mt-1">{{ document?.title || '—' }}</p>
                </div>
                <div>
                  <p class="text-xs font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wide">Status</p>
                  <span class="inline-flex items-center mt-1.5 px-2.5 py-0.5 text-xs font-semibold rounded-full"
                    :class="document?.status === 'active' ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' : 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400'"
                  >
                    {{ document?.status === 'active' ? 'Active' : 'Inactive' }}
                  </span>
                </div>
                <div>
                  <p class="text-xs font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wide">Uploaded By</p>
                  <p class="text-sm font-medium text-gray-800 dark:text-gray-200 mt-1">{{ uploadedBy || '—' }}</p>
                </div>
                <div>
                  <p class="text-xs font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wide">Date Uploaded</p>
                  <p class="text-sm font-medium text-gray-800 dark:text-gray-200 mt-1">
                    {{ document?.created_at ? new Date(document.created_at).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' }) : '—' }}
                  </p>
                </div>
              </div>
            </div>
            <!-- File Preview -->
            <div class="bg-gray-50 dark:bg-gray-700/30 p-5 rounded-xl border border-gray-200 dark:border-gray-600 mb-5">
              <h4 class="text-sm font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-4 border-b border-gray-200 dark:border-gray-600 pb-2">
                <FolderOpen class="h-4 w-4 inline" />
                File Preview
              </h4>
              <div v-if="document?.file_path">
                <div v-if="isPreviewableImage" class="flex items-center justify-center bg-white dark:bg-gray-800 rounded-lg p-2 border border-gray-200 dark:border-gray-600">
                  <img :src="fileUrl" alt="file preview" class="max-w-full max-h-[50vh] rounded object-contain" />
                </div>
                <div v-else-if="isPreviewablePdf" class="rounded-lg overflow-hidden border border-gray-200 dark:border-gray-600">
                  <iframe :src="fileUrl" class="w-full h-[50vh]"></iframe>
                </div>
                <div v-else class="text-center py-8 text-gray-500 border border-dashed rounded-xl">
                  <FileText class="h-12 w-12 mx-auto mb-2 text-gray-300 dark:text-gray-600" />
                  <p class="text-base font-medium">Preview not available</p>
                  <p class="text-sm mt-1">This file type cannot be previewed in the browser.</p>
                </div>
              </div>
              <p v-else class="text-sm text-gray-400 italic">No file attached.</p>
            </div>
          </div>
          <div class="bg-gray-50 dark:bg-gray-700 px-4 py-3 flex flex-col gap-2 sm:px-6 sm:flex-row-reverse sm:gap-0 shrink-0">
            <button
              v-if="document?.file_path"
              @click="handlePrint"
              class="w-full sm:w-auto sm:ml-3 px-4 py-2 rounded-lg text-sm font-semibold text-white bg-purple-600 hover:bg-purple-700 shadow-sm transition-all inline-flex items-center justify-center gap-2"
            >
              <Printer class="h-4 w-4" />
              Print
            </button>
            <button
              v-if="document?.file_path"
              @click="handleDownload"
              class="w-full sm:w-auto sm:ml-3 px-4 py-2 rounded-lg text-sm font-semibold text-white bg-emerald-600 hover:bg-emerald-700 shadow-sm transition-all inline-flex items-center justify-center gap-2"
            >
              <DownloadIcon class="h-4 w-4" />
              Download
            </button>
            <button
              type="button"
              class="w-full sm:w-auto px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 text-sm font-medium text-secondary-text dark:text-secondary-dark-text hover:bg-gray-100/80 dark:hover:bg-gray-700/60 transition-colors"
              @click="$emit('close')"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  </Transition>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { FileText, X, BookCopy, FolderOpen, CloudDownload as DownloadIcon, Printer } from 'lucide-vue-next'
import { employeeDocumentService, type EmployeeDocument } from '@/services/employeeDocumentService'
import { useToast } from 'vue-toastification'

const toast = useToast()
const toastTimeout = 5000

const props = defineProps<{
  isOpen: boolean
  document: EmployeeDocument | null
}>()

defineEmits<{
  (e: 'close'): void
}>()

const uploadedBy = computed(() => {
  if (!props.document?.user) return ''
  const f = props.document.user.details?.first_name || ''
  const l = props.document.user.details?.last_name || ''
  if (f && l) return `${f} ${l}`
  return props.document.user.email || ''
})

const fileUrl = computed(() => {
  if (!props.document?.file_path) return ''
  const baseUrl = (process.env.VUE_APP_API_URL || '').replace(/\/api\/?$/, '')
  return baseUrl + props.document.file_path
})

const isPreviewableImage = computed(() => {
  const ext = (props.document?.file_path || '').toLowerCase().split('.').pop()
  return ['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(ext || '')
})

const isPreviewablePdf = computed(() => {
  return (props.document?.file_path || '').toLowerCase().endsWith('.pdf')
})

const handlePrint = async () => {
  if (!props.document?.id) return
  try {
    await employeeDocumentService.printDocument(props.document.id)
    toast.success('Document sent to printer', { timeout: toastTimeout })
  } catch (err: any) {
    if (!err.message?.includes('opened in a new tab')) {
      toast.error(err.message || 'Failed to print document', { timeout: toastTimeout })
    }
  }
}

const handleDownload = async () => {
  if (!props.document?.id) return
  try {
    const token = localStorage.getItem('token')
    const url = employeeDocumentService.getDownloadUrl(props.document.id)
    const response = await fetch(url, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    })
    if (!response.ok) throw new Error('Invalid File')
    const blob = await response.blob()
    const downloadUrl = window.URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = downloadUrl
    a.download = props.document.title || 'download'
    document.body.appendChild(a)
    a.click()
    window.URL.revokeObjectURL(downloadUrl)
    document.body.removeChild(a)
    toast.success('Document downloaded successfully', { timeout: toastTimeout })
  } catch (err: any) {
    toast.error(err.message || 'Failed to download file', { timeout: toastTimeout })
  }
}
</script>

<style scoped>
.modal-enter-active,
.modal-leave-active {
  transition: opacity 0.2s ease;
}
.modal-enter-active > div > div,
.modal-leave-active > div > div {
  transition: transform 0.2s ease;
}
.modal-enter-from,
.modal-leave-to {
  opacity: 0;
}
.modal-enter-from > div > div,
.modal-leave-to > div > div {
  transform: scale(0.95);
}
</style>
